/* Performance TV — full-screen office display matching the design prototype.
   Live data every 15s (keeps last good figures), wake lock, 7s spotlight sweep,
   ticker of recent activity, push-to-talk Hey Andi, celebration alerts. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBranding } from '../../../hooks/useBranding';
import axios from 'axios';
import { Loader2, Mic, Square, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { useAndiRealtime, RT_STATE } from '../../../hooks/useAndiRealtime';
import { Ring, Roll, Dial, dollars, money, pct0, paceTone } from './Ring';
import { useCelebrations } from './Celebrations';
import './jarvis.css';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL = 15_000;
const curMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

export function useTvData(token) {
  const [data, setData] = useState({ board: null, live: null, need: null });
  const [stale, setStale] = useState(false);
  const [at, setAt] = useState(null);
  const load = useCallback(async () => {
    if (!token) return;
    const H = { headers: { Authorization: `Bearer ${token}` } };
    const m = curMonth();
    try {
      const [b, l, n] = await Promise.all([
        axios.get(`${API}/api/gameplan/board/${m}`, H), axios.get(`${API}/api/gameplan/live/overview?month=${m}`, H), axios.get(`${API}/api/gameplan/lodge-approve-needed`, H),
      ]);
      setData({ board: b.data, live: l.data, need: n.data }); setStale(false); setAt(new Date());
    } catch (_) { setStale(true); }
  }, [token]);
  useEffect(() => {
    load();
    const id = setInterval(load, POLL);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVis); window.addEventListener('online', load);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('online', load); };
  }, [load]);
  return { ...data, stale, at, reload: load };
}

const useClock = () => { const [n, setN] = useState(() => new Date()); useEffect(() => { const id = setInterval(() => setN(new Date()), 1000); return () => clearInterval(id); }, []); return n; };

const statusOf = (d) => { const h = (d.credit?.health || '').toUpperCase(); return h === 'OVERDUE' ? 'red' : h === 'AT RISK' || h === 'ACTION NEEDED' ? 'amber' : 'green'; };

function AndiMic({ context }) {
  const token = localStorage.getItem('auth_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [line, setLine] = useState('');
  const { state, level, connect, disconnect, isLive } = useAndiRealtime({
    headers, extraContext: context,
    onAndiTranscript: (t, done) => setLine((p) => (done ? t : p + t)),
    onError: (m) => toast.error(m),
  });
  const busy = state === RT_STATE.CONNECTING;
  const bars = [0.4, 0.8, 1, 0.7, 0.5];
  return (
    <div className="gpj-tv-mic" data-testid="tv-andi">
      {isLive && <span className="lv" aria-hidden="true">{bars.map((b, i) => <i key={i} style={{ height: `${3 + level * 13 * b}px` }} />)}</span>}
      {isLive && line && <span className="gpj-pill" style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}>{line}</span>}
      <button type="button" className={`gpj-btn icon${isLive ? ' primary' : ''}`} onClick={() => (isLive ? disconnect() : connect())} disabled={busy} title={isLive ? 'Stop Andi' : 'Hey Andi — push to talk'} data-testid="tv-andi-btn">
        {busy ? <Loader2 className="animate-spin" /> : isLive ? <Square /> : <Mic />}
      </button>
    </div>
  );
}

export default function PerformanceTV({ token, onExit, kiosk }) {
  const brand = useBranding();
  const { board, live, need, stale } = useTvData(token);
  const cel = useCelebrations();
  const now = useClock();
  const root = useRef(null);
  const [spot, setSpot] = useState(0);
  const [idle, setIdle] = useState(false);
  const prevIds = useRef(null);
  const [newIds, setNewIds] = useState(new Set());

  // Fullscreen + wake lock; Escape exits (unless a celebration is showing).
  useEffect(() => {
    let wake;
    try { root.current?.requestFullscreen?.().catch(() => {}); } catch (_) { /* not allowed */ }
    const lock = async () => { try { wake = await navigator.wakeLock?.request('screen'); } catch (_) { /* unsupported */ } };
    lock();
    const onVis = () => { if (!document.hidden) lock(); };
    document.addEventListener('visibilitychange', onVis);
    const onKey = (e) => { if (e.key === 'Escape' && !document.querySelector('.gpj-celebrate')) onExit?.(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('visibilitychange', onVis); document.removeEventListener('keydown', onKey); try { wake?.release(); } catch (_) { /* noop */ } try { if (document.fullscreenElement) document.exitFullscreen(); } catch (_) { /* noop */ } };
  }, [onExit]);

  // Hide the cursor after 4s idle, sweep a spotlight every 7s.
  useEffect(() => {
    let t = setTimeout(() => setIdle(true), 4000);
    const move = () => { setIdle(false); clearTimeout(t); t = setTimeout(() => setIdle(true), 4000); };
    window.addEventListener('mousemove', move);
    const id = setInterval(() => setSpot((s) => s + 1), 7000);
    return () => { window.removeEventListener('mousemove', move); clearTimeout(t); clearInterval(id); };
  }, []);

  // Feed → celebrations; new file ids glow on the board.
  useEffect(() => {
    if (!live || !board) return;
    cel?.ingestFeed(live.feed, { lodged: board.monthly_lodged_amount, lodgeTarget: board.monthly_target_amount, approved: board.approved_loan_amount, settled: live.settled?.amount, settleTarget: live.settled?.target });
    const ids = new Set([1, 2, 3, 4].flatMap((w) => (board.board?.[w] || []).map((d) => d.id)));
    if (prevIds.current) { const n = new Set([...ids].filter((i) => !prevIds.current.has(i))); if (n.size) { setNewIds(n); setTimeout(() => setNewIds(new Set()), 4000); } }
    prevIds.current = ids;
  }, [live, board]); // eslint-disable-line

  const target = Number(board?.monthly_target_amount || 0), lodged = Number(board?.monthly_lodged_amount || 0);
  const p = target ? lodged / target : 0, remain = Math.max(0, target - lodged);
  const approved = Number(board?.approved_loan_amount || 0), gci = Number(board?.approved_gci_amount || 0), brokerage = Number(board?.total_brokerage_amount || 0);
  const st = live?.settled || {}, bd = live?.business_days || {};
  const weeks = [1, 2, 3, 4].map((w) => ({ w, files: board?.board?.[w] || [], value: board?.totals?.[w]?.amount || 0, current: live?.weeks?.[w - 1]?.current }));
  const active = weeks.reduce((s, x) => s + x.files.length, 0);
  const { atRisk, overdue } = weeks.reduce((a, x) => { x.files.forEach((d) => { const s = statusOf(d); if (s === 'red') a.overdue++; else if (s === 'amber') a.atRisk++; }); return a; }, { atRisk: 0, overdue: 0 });
  const approvedN = live?.periods?.month?.approved_count ?? 0, apprT = live?.approvals_target || 0;
  const spots = ['hero', 'appr', 'gci', 'settle', 'w1', 'w2', 'w3', 'w4', 'd0', 'd1', 'd2'];
  const lit = spots[spot % spots.length];
  const context = live && board ? `Performance TV live figures: lodged ${dollars(lodged)} of ${dollars(target)} (${pct0(p)}), ${board.monthly_lodged_count} files; approved ${dollars(approved)} (${approvedN} files), GCI ${dollars(gci)}, brokerage ${dollars(brokerage)}; settled ${dollars(st.amount)} of ${dollars(st.target)}; ${bd.left} business days left; at risk ${atRisk}, overdue ${overdue}.` : '';
  const ago = (e) => { if (e.ts) { const h = Math.max(0, Math.round((Date.now() - new Date(e.ts).getTime()) / 3600000)); return h < 1 ? 'just now' : h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`; } const d = Math.round((Date.now() - new Date(e.at + 'T12:00:00').getTime()) / 86400000); return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d}d ago`; };
  const TONE = { lodged: 'var(--blue)', approved: 'var(--cyan)', settled: 'var(--green)' };
  const monthLabel = now.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

  return (
    <div ref={root} className={`gpj gpj-tv${idle ? ' idle' : ''}`} role="dialog" aria-modal="true" aria-label="Performance TV" data-testid="performance-tv">
      <div className="gpj-ambient" aria-hidden="true"><i /><i /><i /><b /></div>
      <header className="gpj-tv-top">
        <div className="gpj-tv-brand"><small>{(brand.business_name || 'DerwentAI').toUpperCase()}</small><h2>Game Plan</h2></div>
        <div className="gpj-tv-meta" data-testid="tv-meta">{monthLabel}{bd.left != null ? ` · ${bd.left} business days left` : ''}</div>
        <span className={`gpj-pill${stale ? ' stale' : ''}`} data-testid="tv-live-pill"><span className="gpj-live-dot" style={stale ? { background: 'var(--amber)', animation: 'none' } : undefined} />{stale ? 'Reconnecting · last good data' : 'Live'}</span>
        {cel && <button type="button" className="gpj-btn icon" onClick={() => cel.sound.setLocalMuted(!cel.sound.localMuted)} title={cel.sound.localMuted ? 'Alert sound muted on this screen' : 'Alert sound on'} data-testid="tv-mute-btn">{cel.sound.localMuted || !cel.settings?.on ? <VolumeX /> : <Volume2 />}</button>}
        <AndiMic context={context} />
        <div className="gpj-tv-clock"><b data-testid="tv-clock">{now.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}</b><small>{now.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}</small></div>
        <button type="button" className="gpj-btn" onClick={onExit} data-testid="tv-exit-btn">{kiosk ? 'Open tracker' : 'Exit'}</button>
      </header>

      <main className="gpj-tv-main">
        <section className={`gpj-tvp gpj-tv-hero${lit === 'hero' ? ' spot' : ''}`} data-testid="tv-hero">
          <div className="gpj-tv-eyebrow">Lodged this month</div>
          <Ring p={p} size={Math.min(300, Math.max(160, window.innerHeight * 0.26))} value={pct0(p)} of={`${money(lodged)} of ${money(target, 0)}`} tone={target ? paceTone(p, live?.elapsed || 0) : 'green'} bare className={lit === 'hero' ? 'spot' : ''} testid="tv-ring-lodged" />
          <div className="gpj-tv-big" data-testid="tv-lodged"><Roll value={dollars(lodged)} /></div>
          <div className="gpj-tv-line">{board?.monthly_lodged_count || 0} files · {pct0(p)} of {dollars(target)}</div>
          <div className="gpj-track"><div className="gpj-fill" style={{ width: `${Math.min(100, p * 100)}%` }} /></div>
          <div className="gpj-tv-hfacts">
            <div><span>Still to lodge</span><strong><Roll value={money(remain)} /></strong></div>
            <div><span>Weekly target</span><strong><Roll value={`${board?.weekly_lodgement_target || 0} /wk`} /></strong></div>
            <div><span>At risk · overdue</span><strong className={atRisk + overdue ? 'warn' : ''}><Roll value={`${atRisk} · ${overdue}`} /></strong></div>
          </div>
        </section>

        <section className="gpj-tv-right">
          <div className="gpj-tv-kpis">
            <div className={`gpj-tvp gpj-kpi${lit === 'appr' ? ' spot' : ''}`} data-testid="tv-kpi-approved"><span><i style={{ background: 'var(--green)' }} />Approved this month</span><strong><Roll value={dollars(approved)} /></strong><em>{approvedN}{apprT ? ` of ${apprT}` : ''} approvals · {dollars(brokerage)} brokerage</em><div className="gpj-track"><div className="gpj-fill" style={{ width: `${Math.min(100, (apprT ? approvedN / apprT : 0) * 100)}%` }} /></div></div>
            <div className={`gpj-tvp gpj-kpi${lit === 'gci' ? ' spot' : ''}`} data-testid="tv-kpi-gci"><span><i style={{ background: 'var(--cyan)' }} />Approved GCI</span><strong><Roll value={dollars(gci)} /></strong><em>At 0.66% upfront on approved volume</em><div className="gpj-track"><div className="gpj-fill cyan" style={{ width: `${Math.min(100, (target ? gci / (target * 0.0066) : 0) * 100)}%` }} /></div></div>
            <div className={`gpj-tvp gpj-kpi${lit === 'settle' ? ' spot' : ''}`} data-testid="tv-kpi-settled"><span><i style={{ background: 'var(--green)' }} />Settled this month</span><strong><Roll value={money(st.amount, 2)} /></strong><em>{st.target ? `${pct0((st.amount || 0) / st.target)} of ${money(st.target, 0)} · ${money(st.needed_per_bd)} a day needed` : 'Set a settlement target in Set Goals'}</em><div className="gpj-track"><div className="gpj-fill" style={{ width: `${Math.min(100, (st.target ? (st.amount || 0) / st.target : 0) * 100)}%` }} /></div></div>
          </div>

          <div className="gpj-tvp gpj-tv-board" data-testid="tv-board">
            <div className="gpj-tv-board-h"><b>Weekly Pipeline Board</b><span>{active} active files · {money(weeks.reduce((s, x) => s + x.files.reduce((a, d) => a + Number(d.est_loan_amount || 0), 0), 0))} in flight</span></div>
            <div className="gpj-tv-weeks">
              {weeks.map((x) => {
                const share = target / 4, shown = x.files.slice(0, 5);
                return (
                  <div key={x.w} className={`gpj-tvw${x.current ? ' current' : ''}${lit === `w${x.w}` ? ' spot' : ''}`} data-testid={`tv-week-${x.w}`}>
                    <div className="gpj-tvw-h">Week {x.w}<small>{x.files.length} files</small></div>
                    <strong><Roll value={money(x.value, 2)} /></strong>
                    <div className={`gpj-wbar${share && x.value >= share ? ' done' : ''}`}><i style={{ width: `${share ? Math.min(100, (x.value / share) * 100) : 0}%` }} /></div>
                    <div className="gpj-tvw-files">
                      {shown.length ? shown.map((d) => { const s = statusOf(d); return <div key={d.id} className={`gpj-tvf${s === 'green' ? '' : ` ${s}`}${newIds.has(d.id) ? ' new' : ''}`}><b>{money(d.est_loan_amount, 2)}</b><span>{d.client_name}</span></div>; }) : <div className="gpj-tvw-more">No files lodged yet</div>}
                      {x.files.length > 5 && <div className="gpj-tvw-more">+{x.files.length - 5} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="gpj-tvp gpj-tv-needed" data-testid="tv-needed">
            {[['today', 'day'], ['this week', 'week'], ['this month', 'month']].map(([t, k], i) => (
              <div className={`gpj-tvn${lit === `d${i}` ? ' spot' : ''}`} key={k}>
                <small>Still needed {t}</small>
                <div className="pair">
                  <Dial remain={need?.lodge?.[k]?.remaining ?? 0} done={need?.lodge?.[k]?.done ?? 0} target={need?.lodge?.[k]?.target ?? 0} label="To lodge" testid={`tv-dial-lodge-${k}`} />
                  <Dial remain={need?.approve?.[k]?.remaining ?? 0} done={need?.approve?.[k]?.done ?? 0} target={need?.approve?.[k]?.target ?? 0} label="To approve" green testid={`tv-dial-approve-${k}`} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="gpj-ticker" data-testid="tv-ticker">
        <div className="gpj-ticker-in">
          {(live?.feed || []).slice(0, 12).map((e) => <span key={e.key}><i style={{ background: TONE[e.type] }} />{dollars(e.amount)} {e.type} · {e.client}{e.lender ? ` · ${e.lender}` : ''} · {ago(e)}</span>)}
          {!live?.feed?.length && <span>Lodgements, approvals and settlements will appear here as they happen.</span>}
        </div>
      </footer>
    </div>
  );
}
